import { FC, useEffect, useState } from 'react';
import { SavedMedium, SaveMedium } from '@zoopla-eng/zds-components/icons';
import { fontGroups } from '@zoopla-eng/zds-components/themes/font-groups';
import { ButtonLeft } from '@zoopla-eng/zds-components/button';
import { AuthModal } from '@zoopla/authentication/modal';
import { useAuthModal } from '@zoopla/authentication/use-auth-modal';
import {
    ConsentAndRelationshipModal,
    useDeleteTrackedPropertyMutation,
} from '@zoopla/property-relationship/consent-and-relationship-modal';
import {
    type BaseTaxonomy,
    EventNames,
    type Taxonomy,
    trackSegmentAndGAEvent,
} from '@zoopla/tracking';
import { useIsLoggedIn } from '@zoopla/user';
import { useAbortSignal } from '@zoopla/shared/async-hooks';
import {
    type TrackedProperty,
    getTrackedProperty,
    getTaxonomyListingId,
} from '../../helpers';
import { TrackedPropertyRelationship } from '../../graphql.generated';
import {
    containerStyle,
    addressStyle,
    blurbStyle,
    ctaWrapperStyle,
    errorStyle,
} from './TrackProperty.styles.css';

const HEADING_ID = 'valuation-success-track-property-heading';
const ERROR_ID = 'valuation-success-track-property-error';

const getSegmentData = (baseTaxonomy: BaseTaxonomy) => {
    const { page, section, activity } = baseTaxonomy;
    return { page, section, activity };
};

export type TrackPropertyProps = {
    fullAddress: string;
    uprn: string;
    baseTaxonomy: BaseTaxonomy;
};

const TrackProperty: FC<TrackPropertyProps> = ({
    fullAddress,
    uprn,
    baseTaxonomy,
}) => {
    const [isAuthModalShowing, openAuthModal, dismissAuthModal] =
        useAuthModal();

    const initialIsLoggedIn = useIsLoggedIn();

    // useIsLoggedIn doesn't update when a user's state changes so we store the
    // intial value and then update it when they are authenticated to ensure
    // that the use effect that handles updating the "initial" (i.e. after auth)
    // state for the button
    const [isLoggedIn, setIsLoggedIn] = useState(initialIsLoggedIn);

    const abortSignal = useAbortSignal();

    const [deleteTrackedProperty] = useDeleteTrackedPropertyMutation();

    const [isRelationshipModalOpen, setIsRelationshipModalOpen] =
        useState(false);
    const [property, setProperty] = useState<TrackedProperty | null>(null);
    const [isLoading, setIsLoading] = useState(isLoggedIn);
    const [hasError, setHasError] = useState(false);

    const isTracked = !!property?.isTracked;

    useEffect(() => {
        const abortController = new AbortController();

        const asyncGetProperty = async () => {
            if (!uprn || !isLoggedIn) return;

            const property = await getTrackedProperty(uprn, {
                signal: abortController.signal,
            });
            setProperty(property);
            setIsLoading(false);
        };

        asyncGetProperty();

        return () => {
            abortController.abort();
        };
    }, [uprn, isLoggedIn]);

    const onAuthClose = async (hasAuthenticated: boolean) => {
        dismissAuthModal();

        if (hasAuthenticated) {
            setIsRelationshipModalOpen(true);
            setIsLoggedIn(true);
        }
    };

    const onTrackButtonClick = async () => {
        if (isLoading) return;

        // If they're not logged in, show them the auth modal to complete first,
        // the callback that is called on close of the auth modal then handles
        // showing the consent modal (if auth was successful).
        if (!isLoggedIn) {
            openAuthModal(null);
            return;
        }

        // If the property is untracked, show them the consent modal to track it
        // in which all of the logic for tracking the property is held.
        if (!isTracked) {
            setIsRelationshipModalOpen(true);
            return;
        }

        // If not, try and untrack the tracked property using the shared
        // deleteTrackedProperty query.
        setIsLoading(true);
        setHasError(false);

        try {
            await deleteTrackedProperty({
                variables: {
                    uprn,
                },
            });

            setProperty(null);
            setIsLoading(false);
        } catch (err) {
            if (!abortSignal.current?.aborted) {
                setHasError(true);
                setIsLoading(false);
            }
        }
    };

    const onRelationshipModalSuccess = (
        relationship?: TrackedPropertyRelationship
    ) => {
        // While property is null, isLoading is always true so the modal can't
        // be opened or completed. We have to satisfy the type of "could be
        // null" within the setter, but this will never happen in practice.
        if (!property) return;

        setProperty({
            ...property,
            isTracked: true,
        });
        setIsRelationshipModalOpen(false);

        const taxonomy: Taxonomy = {
            ...baseTaxonomy,
            uprn,
            property_relationship: relationship,
            price: property.saleEstimate?.currentPrice,
            rent: property.rentEstimate?.currentPrice,
            outcode: property.outcode,
            postcode: property.postcode,
            num_beds: property.bedrooms,
            num_baths: property.bathrooms,
            listing_id: getTaxonomyListingId(property.liveListingIds),
        };
        trackSegmentAndGAEvent(
            EventNames.TRACK_PROPERTY,
            {
                eventCategory: 'valuation_form',
                eventAction: 'click',
                eventLabel: 'track_property',
                eventValue: uprn,
            },
            taxonomy.page,
            taxonomy
        );
    };

    const onRelationshipModalClose = () => {
        setIsRelationshipModalOpen(false);
        setIsLoading(false);
    };

    return (
        <>
            <section className={containerStyle} aria-labelledby={HEADING_ID}>
                <h2 className={fontGroups.kettle} id={HEADING_ID}>
                    Track this property
                </h2>
                <p
                    className={`${addressStyle} ${fontGroups.whisk}`}
                    data-cs-mask
                >
                    {fullAddress}
                </p>
                <p className={`${blurbStyle} ${fontGroups.ladle}`}>
                    Follow your home and keep up to date with other homes you
                    are interested in.
                </p>
                <div className={ctaWrapperStyle}>
                    <ButtonLeft
                        category="primary"
                        onClick={onTrackButtonClick}
                        Icon={isTracked ? SavedMedium : SaveMedium}
                        loading={isLoading}
                        loadingText="Loading…"
                        selected={isTracked && !isLoading}
                        aria-describedby={hasError ? ERROR_ID : undefined}
                    >
                        {isTracked ? 'Tracked' : 'Track this property'}
                    </ButtonLeft>

                    {hasError && (
                        <p
                            role="alert"
                            id={ERROR_ID}
                            className={`${errorStyle} ${fontGroups.knife}`}
                        >
                            Something went wrong, please try again.
                        </p>
                    )}
                </div>
            </section>

            <ConsentAndRelationshipModal
                isOpen={isRelationshipModalOpen}
                close={onRelationshipModalClose}
                onSave={onRelationshipModalSuccess}
                address={fullAddress}
                uprn={uprn}
            />

            <AuthModal
                isShowing={isAuthModalShowing}
                close={onAuthClose}
                trackingData={{
                    label: 'save_property',
                    segmentData: getSegmentData(baseTaxonomy),
                }}
                modalAuthHeading="Track properties"
            />
        </>
    );
};

export default TrackProperty;
